import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { Logo } from "./Logo";
import { NAV } from "@/content/site";
import { useUI } from "@/context/UIContext";
import { whatsappLink, MAILTO, EMAIL, WHATSAPP_DISPLAY } from "@/lib/api";

export function useGoTo() {
  const nav = useNavigate();
  const { pathname } = useLocation();
  return (id) => {
    if (pathname !== "/") {
      nav(`/#${id}`);
      setTimeout(() => document.getElementById(id)?.scrollIntoView(), 80);
      return;
    }
    document.getElementById(id)?.scrollIntoView();
    window.history.replaceState(null, "", `#${id}`);
  };
}

export default function Header() {
  const { openDemo } = useUI();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const goTo = useGoTo();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 8);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  const jump = (id) => { setOpen(false); setTimeout(() => goTo(id), 10); };

  return (
    <header data-testid="site-header" className={`fixed inset-x-0 top-0 z-40 transition-[background-color,box-shadow] duration-300 ${scrolled ? "bg-paper/85 shadow-[0_1px_0_#E2E0D8] backdrop-blur-xl" : "bg-paper/0"}`}>
      <div className="wrap flex h-[72px] items-center justify-between gap-6">
        <Logo />
        <nav aria-label="Hauptnavigation" className="hidden items-center gap-1 lg:flex">
          {NAV.map((n) => (
            <a key={n.id} href={`/#${n.id}`} data-testid={`nav-link-${n.id}`} onClick={(e) => { e.preventDefault(); goTo(n.id); }}
              className="relative rounded-full px-3.5 py-2 text-[14px] font-medium text-ink/80 transition-colors hover:text-ink after:absolute after:inset-x-3.5 after:bottom-1 after:h-px after:origin-left after:scale-x-0 after:bg-gold after:transition-transform hover:after:scale-x-100">
              {n.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <button type="button" data-testid="header-demo-cta" onClick={() => openDemo()} className="btn btn-ink hidden !min-h-[42px] !px-5 !text-[14px] sm:inline-flex">
            Kostenlose Demo anfordern
          </button>
          <Dialog.Root open={open} onOpenChange={setOpen}>
            <Dialog.Trigger asChild>
              <button type="button" data-testid="mobile-menu-open" aria-label="Menü öffnen" className="grid h-11 w-11 place-items-center rounded-full border border-line bg-white/70 lg:hidden">
                <Menu className="h-5 w-5" />
              </button>
            </Dialog.Trigger>
            <Dialog.Portal>
              <Dialog.Overlay className="fixed inset-0 z-50 bg-ink/30 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
              <Dialog.Content data-testid="mobile-menu" className="fixed inset-y-0 right-0 z-50 flex w-full max-w-[420px] flex-col bg-paper p-6 shadow-2xl data-[state=open]:animate-in data-[state=open]:slide-in-from-right data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right">
                <div className="flex items-center justify-between">
                  <Dialog.Title className="eyebrow text-mute">Menü</Dialog.Title>
                  <Dialog.Description className="sr-only">Navigation der Webhaus-Website</Dialog.Description>
                  <Dialog.Close asChild>
                    <button type="button" data-testid="mobile-menu-close" aria-label="Menü schließen" className="grid h-11 w-11 place-items-center rounded-full border border-line"><X className="h-5 w-5" /></button>
                  </Dialog.Close>
                </div>
                <nav aria-label="Mobile Navigation" className="mt-8 flex flex-col">
                  {NAV.map((n, i) => (
                    <button key={n.id} type="button" data-testid={`mobile-nav-${n.id}`} onClick={() => jump(n.id)}
                      className="flex items-center justify-between border-b border-line py-4 text-left font-display text-[28px] font-semibold tracking-tight">
                      <span><span className="mr-3 font-mono text-xs text-mute">0{i + 1}</span>{n.label}</span>
                      <ArrowUpRight className="h-5 w-5 text-gold-deep" />
                    </button>
                  ))}
                </nav>
                <div className="mt-auto space-y-3 pt-8">
                  <button type="button" data-testid="mobile-menu-demo-cta" onClick={() => { setOpen(false); openDemo(); }} className="btn btn-ink w-full">Kostenlose Demo anfordern</button>
                  <div className="grid grid-cols-2 gap-3">
                    <a href={whatsappLink()} target="_blank" rel="noopener noreferrer" data-testid="mobile-menu-whatsapp" className="btn btn-line !text-[14px]" aria-label={`WhatsApp ${WHATSAPP_DISPLAY}`}>WhatsApp</a>
                    <a href={MAILTO} data-testid="mobile-menu-email" className="btn btn-line !text-[14px]" aria-label={`E-Mail an ${EMAIL}`}>E-Mail</a>
                  </div>
                </div>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>
        </div>
      </div>
    </header>
  );
}
